import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: #000;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: #000;
`;

const SearchContainer = styled.div`
  display: flex;
  max-width: 800px;
  margin: 0 auto 3rem;
  position: relative;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 1rem 1.5rem;
  border: 1px solid #ddd;
  border-radius: 50px;
  font-size: 1rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent || '#4a90e2'};
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const FilterButton = styled.button`
  background: #f0f0f0;
  border: none;
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  margin-left: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #333;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const NutritionistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const NutritionistCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;

const NutritionistImage = styled.div`
  height: 240px;
  background-color: #f5f5f5;
  background-image: ${props => props.image ? `url(${props.image})` : 'none'};
  background-size: cover;
  background-position: center;
  position: relative;
`;

const SpecialtyTag = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: ${props => props.theme?.colors?.primary || '#4a90e2'};
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const NutritionistInfo = styled.div`
  padding: 1.5rem;
`;

const NutritionistName = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
  color: #000;
`;

const NutritionistCredentials = styled.p`
  font-size: 0.9rem;
  color: #000;
  margin-bottom: 1rem;
`;

const NutritionistBio = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #000;
  margin-bottom: 1.5rem;
`;

const BookButton = styled.button`
  background: ${props => props.theme?.colors?.primary || '#4a90e2'};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.8rem 0;
  width: 100%;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:hover {
    background: ${props => props.theme?.colors?.primary || '#4a90e2'};
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  background: #ffebee;
  border-radius: 8px;
  margin: 2rem 0;
  color: #c62828;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background: #f9f9f9;
  border-radius: 12px;
  margin: 2rem 0;
`;

const EmptyStateText = styled.p`
  color: #666;
  margin: 1rem 0;
  font-size: 1.1rem;
`;

const Nutritionists = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [nutritionists, setNutritionists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNutritionists = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, 'nutritionists'));
        const nutritionistsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setNutritionists(nutritionistsList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching nutritionists:', error);
        setError('Failed to load nutritionists. Please try again later.');
        setLoading(false);
      }
    };

    fetchNutritionists();
  }, []);

  const filteredNutritionists = nutritionists.filter(nutritionist =>
    nutritionist.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nutritionist.specialty?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageContainer>
      <HeroSection>
        <Title>Hitta din Wonder Klinik</Title>
        <Subtitle>
          Connect with expert nutritionists who specialize in your unique health needs
        </Subtitle>
        
        <SearchContainer>
          <SearchInput 
            type="text" 
            placeholder="Search by name or specialty..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FilterButton>
            <span>Filter</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H2L7 8.5V13L9 14V8.5L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </FilterButton>
        </SearchContainer>
      </HeroSection>
      
      {loading ? (
        <LoadingContainer>
          <p>Loading nutritionists...</p>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <p>{error}</p>
        </ErrorContainer>
      ) : filteredNutritionists.length > 0 ? (
        <NutritionistGrid>
          {filteredNutritionists.map(nutritionist => (
            <NutritionistCard key={nutritionist.id}>
              <NutritionistImage image={nutritionist.imageUrl}>
                <SpecialtyTag>{nutritionist.specialty}</SpecialtyTag>
              </NutritionistImage>
              <NutritionistInfo>
                <NutritionistName>{nutritionist.name}</NutritionistName>
                <NutritionistCredentials>{nutritionist.credentials}</NutritionistCredentials>
                <NutritionistBio>{nutritionist.bio}</NutritionistBio>
                <BookButton>Book a Consultation</BookButton>
              </NutritionistInfo>
            </NutritionistCard>
          ))}
        </NutritionistGrid>
      ) : (
        <EmptyState>
          <EmptyStateText>
            {searchTerm 
              ? `No nutritionists found matching "${searchTerm}"`
              : "No nutritionists available at the moment. Please check back later."}
          </EmptyStateText>
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default Nutritionists; 